import { useEffect, useState } from 'react';
import { Alert, Anchor, Button, Grid, Group, NumberInput, Paper, Select, Stack, Text, TextInput, Tooltip } from '@mantine/core';
import { IconAlertTriangle, IconCircleCheck, IconRefresh } from '@tabler/icons-react';
import { Link } from '@tanstack/react-router';
import { dryKg } from '@zebra/core';
import { ApiRequestError } from '../../../api/client';
import { useCanOperate } from '../../../auth/permissions';
import { PageHeader } from '../../../components/PageHeader';
import { parseNum } from '../../../lib/parseNum';
import { QuickConversionError, useAllProducts, useLocations, useQuickConversion, useRecipesForStage, useSites, useStages } from '../api';
import { formatMoneyCents, nowForDatetimeLocal, qtyInputLabel } from '../format';
import type { ProductDto } from '../types';

/**
 * Reactive prefill (Stuart ruling: type ONE number, the rest follows the
 * recipe's ratio). Mirrors the two-step math ClosePostPanel's live yield
 * preview uses — `dryKg` from `@zebra/core` for the decimal-sensitive wet->dry
 * step, plain JS number arithmetic for the rest (same division as
 * `sumKgDryKg`/`yieldDryPct` callers there, never a hand-rolled Decimal).
 *   outputDry = dryKg(inputKg, inputMc) x expectedYieldDryPct/100
 *   wetOut    = outputDry / (1 - mcOut/100)     [mcOut null => dry=wet]
 * `mcOut` is the RECIPE output line's `expectedMcPct` (config, not the form's
 * own optional output-mc field, which only affects what's actually recorded
 * on the output line) — Howick's products are typically untracked, so both
 * default to null and the whole thing degenerates to `output = input x yield%`.
 * Returns null when there's nothing sensible to prefill (no input qty yet, or
 * a >=100% moisture config that would divide by zero/negative).
 */
function computeOutputWetKg(
  inputQtyKg: string,
  inputMcPct: string | null,
  expectedYieldDryPct: string,
  outputExpectedMcPct: string | null,
): number | null {
  const outputDryKg = Number(dryKg(inputQtyKg, inputMcPct)) * (Number(expectedYieldDryPct) / 100);
  if (!Number.isFinite(outputDryKg)) return null;
  if (outputExpectedMcPct === null) return outputDryKg;
  const mc = Number(outputExpectedMcPct);
  if (!Number.isFinite(mc) || mc >= 100) return null;
  return outputDryKg / (1 - mc / 100);
}

function productName(productsById: Map<string, ProductDto>, productId: string): string {
  return productsById.get(productId)?.name ?? productId;
}

/**
 * "Quick conversion" (Stuart ruling, 2026-08-26): Howick runs ~8 conversions/
 * day (e.g. kiln drying: wet logs -> dried logs) but does NOT weigh loads in/
 * out — operators estimate via the stage's active recipe's historic ratio.
 * ONE screen: pick the stage, type ONE number (input kg), the recipe's
 * expected yield pre-fills the output kg (editable); submitting books a
 * NORMAL production run via the SAME five endpoints RecordShiftWizard drives
 * one step at a time, composed into a single action by `useQuickConversion`
 * (api.ts) — no new API surface, no new posting path.
 */
export function QuickConversionForm() {
  const canOperate = useCanOperate();

  const [siteId, setSiteId] = useState<string | null>(null);
  const [stageId, setStageId] = useState<string | null>(null);
  const [inputProductId, setInputProductId] = useState<string | null>(null);
  const [outputProductId, setOutputProductId] = useState<string | null>(null);
  const [fromLocationId, setFromLocationId] = useState<string | null>(null);
  const [toLocationId, setToLocationId] = useState<string | null>(null);
  const [consumablesFromLocationId, setConsumablesFromLocationId] = useState<string | null>(null);
  const [inputQtyKg, setInputQtyKg] = useState<number | string>('');
  const [inputMcPct, setInputMcPct] = useState<number | string>('');
  const [outputQtyKg, setOutputQtyKg] = useState<number | string>('');
  const [outputMcPct, setOutputMcPct] = useState<number | string>('');
  const [outputTouched, setOutputTouched] = useState(false);
  const [occurredAt, setOccurredAt] = useState(nowForDatetimeLocal());
  const [shiftLabel, setShiftLabel] = useState('');
  const [notes, setNotes] = useState('');

  const sitesQuery = useSites();
  const sites = sitesQuery.data?.items ?? [];
  const stagesQuery = useStages();
  const stages = (stagesQuery.data?.items ?? []).filter((s) => s.isActive && (!siteId || s.siteId === siteId));
  const recipesQuery = useRecipesForStage(stageId ?? undefined);
  const activeRecipe = recipesQuery.data?.items.find((r) => r.isActive);
  const locationsQuery = useLocations(siteId ?? undefined);
  const locations = locationsQuery.data?.items ?? [];
  // Recipe product ids are pinned config, not a general picker — resolved via
  // the un-filtered lookup (api.ts's useAllProducts doc) so a since-
  // deactivated recipe product still shows a name instead of a bare id.
  const allProductsQuery = useAllProducts();
  const productsById = new Map((allProductsQuery.data?.items ?? []).map((p) => [p.id, p]));

  const quickConversion = useQuickConversion();

  // Preselect the site once there's exactly one (Howick, the go-live target,
  // has one) — zero extra clicks for the common case.
  useEffect(() => {
    if (siteId || sites.length !== 1) return;
    setSiteId(sites[0]!.id);
  }, [siteId, sites]);

  // Default the input/output product to the recipe's first input / primary
  // output whenever a new active recipe resolves (stage switched) — mirrors
  // RecipeForm's own default-first-row convention. Keeps a still-valid prior
  // selection (e.g. the query just refetching) instead of clobbering it.
  useEffect(() => {
    if (!activeRecipe) return;
    setInputProductId((prev) =>
      prev && activeRecipe.inputs.some((i) => i.productId === prev) ? prev : (activeRecipe.inputs[0]?.productId ?? null),
    );
    setOutputProductId((prev) => {
      if (prev && activeRecipe.outputs.some((o) => o.productId === prev)) return prev;
      return (activeRecipe.outputs.find((o) => o.isPrimary) ?? activeRecipe.outputs[0])?.productId ?? null;
    });
  }, [activeRecipe]);

  const parsedInputQtyKg = parseNum(inputQtyKg);
  const parsedInputMcPct = parseNum(inputMcPct);
  const parsedOutputQtyKg = parseNum(outputQtyKg);
  const parsedOutputMcPct = parseNum(outputMcPct);

  const selectedRecipeOutput = activeRecipe?.outputs.find((o) => o.productId === outputProductId);
  const inputProduct = inputProductId ? productsById.get(inputProductId) : undefined;
  const outputProduct = outputProductId ? productsById.get(outputProductId) : undefined;

  // Reactive prefill — stops the moment the operator edits the output field
  // themselves (`outputTouched`), same touched-flag precedent as
  // DispatchWizard's price-from-contract prefill.
  useEffect(() => {
    if (outputTouched || !activeRecipe || parsedInputQtyKg === null) return;
    const computed = computeOutputWetKg(
      parsedInputQtyKg.toFixed(3),
      parsedInputMcPct === null ? null : parsedInputMcPct.toFixed(2),
      activeRecipe.expectedYieldDryPct,
      selectedRecipeOutput?.expectedMcPct ?? null,
    );
    if (computed !== null) setOutputQtyKg(computed.toFixed(3));
  }, [outputTouched, activeRecipe, parsedInputQtyKg, parsedInputMcPct, selectedRecipeOutput]);

  function handleStageChange(value: string | null) {
    setStageId(value);
    setInputProductId(null);
    setOutputProductId(null);
    setOutputQtyKg('');
    setOutputTouched(false);
  }

  function handleOutputProductChange(value: string | null) {
    setOutputProductId(value);
    // A different output product has a different (or no) expected MC — the
    // previously prefilled/edited wet-out figure no longer means anything.
    setOutputQtyKg('');
    setOutputTouched(false);
  }

  const hasConsumables = (activeRecipe?.consumables.length ?? 0) > 0;
  const noActiveRecipe = Boolean(stageId) && Boolean(recipesQuery.data) && !activeRecipe;

  const canSubmit =
    canOperate &&
    Boolean(siteId && stageId && activeRecipe) &&
    Boolean(inputProductId && fromLocationId) &&
    Boolean(outputProductId && toLocationId) &&
    parsedInputQtyKg !== null &&
    parsedInputQtyKg > 0 &&
    parsedOutputQtyKg !== null &&
    parsedOutputQtyKg > 0 &&
    (!hasConsumables || Boolean(consumablesFromLocationId));

  function handleSubmit() {
    if (
      !canSubmit ||
      !siteId ||
      !stageId ||
      !inputProductId ||
      !fromLocationId ||
      !outputProductId ||
      !toLocationId ||
      parsedInputQtyKg === null ||
      parsedOutputQtyKg === null
    ) {
      return;
    }
    quickConversion.mutate({
      siteId,
      stageId,
      shiftLabel: shiftLabel.trim() || null,
      notes: notes.trim() || null,
      occurredAt: occurredAt ? new Date(occurredAt) : null,
      inputProductId,
      fromLocationId,
      inputQtyKg: parsedInputQtyKg.toFixed(3),
      inputMcPct: parsedInputMcPct === null ? null : parsedInputMcPct.toFixed(2),
      outputProductId,
      toLocationId,
      outputQtyKg: parsedOutputQtyKg.toFixed(3),
      outputMcPct: parsedOutputMcPct === null ? null : parsedOutputMcPct.toFixed(2),
      consumablesFromLocationId: hasConsumables ? consumablesFromLocationId : null,
    });
  }

  function resetForm() {
    setStageId(null);
    setInputProductId(null);
    setOutputProductId(null);
    setFromLocationId(null);
    setToLocationId(null);
    setConsumablesFromLocationId(null);
    setInputQtyKg('');
    setInputMcPct('');
    setOutputQtyKg('');
    setOutputMcPct('');
    setOutputTouched(false);
    setOccurredAt(nowForDatetimeLocal());
    setShiftLabel('');
    setNotes('');
    quickConversion.reset();
  }

  // The composite mutation re-throws a step-2+ failure as `QuickConversionError`
  // carrying the already-OPENED run's id (api.ts) — an initial-open failure (no
  // active recipe, bad input) is a plain `ApiRequestError` with no run to link.
  const partialFailureRunId = quickConversion.error instanceof QuickConversionError ? quickConversion.error.runId : null;

  return (
    <Stack gap="lg" maw={720}>
      <PageHeader
        title="Quick conversion"
        description="Ratio-driven conversion entry: type the input weight, the active recipe's expected yield estimates the output — no weighing required."
      />
      <Paper withBorder radius="md" p="lg">
        <Stack gap="sm">
          <Grid>
            <Grid.Col span={{ base: 12, sm: 6 }}>
              <Select
                label="Site"
                placeholder="Site"
                data={sites.map((s) => ({ value: s.id, label: s.name }))}
                value={siteId}
                onChange={setSiteId}
                disabled={sitesQuery.isLoading}
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 6 }}>
              <Select
                label="Stage"
                placeholder="Choose a stage"
                data={stages.map((s) => ({ value: s.id, label: s.name }))}
                value={stageId}
                onChange={handleStageChange}
                searchable
                disabled={!siteId || stagesQuery.isLoading}
              />
            </Grid.Col>
          </Grid>

          {noActiveRecipe && (
            <Alert color="amber" icon={<IconAlertTriangle size={18} />} title="No active recipe">
              This stage has no active recipe to estimate a conversion ratio from.{' '}
              <Link to="/production-packconv/stages">
                <Anchor component="span" size="sm">
                  Activate one on the Stages &amp; recipes tab
                </Anchor>
              </Link>{' '}
              first.
            </Alert>
          )}

          {activeRecipe && (
            <Text size="sm" c="dimmed">
              {activeRecipe.inputs.map((i) => productName(productsById, i.productId)).join(' + ')}
              {' → '}
              {productName(productsById, (activeRecipe.outputs.find((o) => o.isPrimary) ?? activeRecipe.outputs[0])!.productId)}
              {' · expected yield '}
              {activeRecipe.expectedYieldDryPct}%
            </Text>
          )}

          {activeRecipe && activeRecipe.inputs.length > 1 && (
            <Select
              label="Input product"
              data={activeRecipe.inputs.map((i) => ({ value: i.productId, label: productName(productsById, i.productId) }))}
              value={inputProductId}
              onChange={(v) => {
                setInputProductId(v);
                setOutputQtyKg('');
                setOutputTouched(false);
              }}
            />
          )}
          {activeRecipe && activeRecipe.outputs.length > 1 && (
            <Select
              label="Output product"
              data={activeRecipe.outputs.map((o) => ({ value: o.productId, label: productName(productsById, o.productId) }))}
              value={outputProductId}
              onChange={handleOutputProductChange}
            />
          )}

          <Grid>
            <Grid.Col span={{ base: 6, sm: 3 }}>
              <NumberInput
                label={qtyInputLabel(inputProduct?.baseUom, 'Input (kg)', 'Input (units)')}
                decimalScale={3}
                min={0}
                value={inputQtyKg}
                onChange={setInputQtyKg}
                disabled={!activeRecipe}
              />
            </Grid.Col>
            <Grid.Col span={{ base: 6, sm: 3 }}>
              <NumberInput
                label="Input moisture %"
                decimalScale={2}
                min={0}
                max={100}
                value={inputMcPct}
                onChange={setInputMcPct}
                disabled={!activeRecipe}
              />
            </Grid.Col>
            <Grid.Col span={{ base: 6, sm: 3 }}>
              <NumberInput
                label={qtyInputLabel(outputProduct?.baseUom, 'Output (kg) — estimated', 'Output (units) — estimated')}
                decimalScale={3}
                min={0}
                value={outputQtyKg}
                onChange={(v) => {
                  setOutputQtyKg(v);
                  setOutputTouched(true);
                }}
                disabled={!activeRecipe}
              />
            </Grid.Col>
            <Grid.Col span={{ base: 6, sm: 3 }}>
              <NumberInput
                label="Output moisture %"
                decimalScale={2}
                min={0}
                max={100}
                value={outputMcPct}
                onChange={setOutputMcPct}
                disabled={!activeRecipe}
              />
            </Grid.Col>
          </Grid>
          {outputTouched && (
            <Button
              variant="subtle"
              size="xs"
              leftSection={<IconRefresh size={14} />}
              onClick={() => setOutputTouched(false)}
              style={{ alignSelf: 'flex-start' }}
            >
              Recalculate from recipe
            </Button>
          )}

          <Grid>
            <Grid.Col span={{ base: 12, sm: 6 }}>
              <Select
                label="From location"
                placeholder="Location"
                data={locations.map((l) => ({ value: l.id, label: `${l.code} — ${l.name}` }))}
                value={fromLocationId}
                onChange={setFromLocationId}
                searchable
                disabled={!siteId || locationsQuery.isLoading}
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 6 }}>
              <Select
                label="To location"
                placeholder="Location"
                data={locations.map((l) => ({ value: l.id, label: `${l.code} — ${l.name}` }))}
                value={toLocationId}
                onChange={setToLocationId}
                searchable
                disabled={!siteId || locationsQuery.isLoading}
              />
            </Grid.Col>
          </Grid>

          {hasConsumables && (
            <Select
              label="Consumables from"
              description={`This recipe has ${activeRecipe!.consumables.length} consumable(s) backflushed at close — choose where to book them from.`}
              placeholder="Choose a location"
              data={locations.map((l) => ({ value: l.id, label: `${l.code} — ${l.name}` }))}
              value={consumablesFromLocationId}
              onChange={setConsumablesFromLocationId}
              searchable
            />
          )}

          <Grid>
            <Grid.Col span={{ base: 12, sm: 4 }}>
              <TextInput
                label="Date/time (optional)"
                type="datetime-local"
                value={occurredAt}
                onChange={(e) => setOccurredAt(e.currentTarget.value)}
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 4 }}>
              <TextInput
                label="Shift label (optional)"
                placeholder="e.g. Day shift"
                value={shiftLabel}
                onChange={(e) => setShiftLabel(e.currentTarget.value)}
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 4 }}>
              <TextInput label="Notes (optional)" value={notes} onChange={(e) => setNotes(e.currentTarget.value)} />
            </Grid.Col>
          </Grid>

          {quickConversion.isError && (
            <Alert
              color="red"
              icon={<IconAlertTriangle size={16} />}
              title={partialFailureRunId ? 'Run opened but not completed' : 'Quick conversion failed'}
            >
              {quickConversion.error instanceof ApiRequestError || quickConversion.error instanceof QuickConversionError
                ? quickConversion.error.message
                : 'Failed to record the conversion'}
              {partialFailureRunId && (
                <>
                  {' '}
                  <Link to="/production-packconv/runs/$runId" params={{ runId: partialFailureRunId }}>
                    <Anchor component="span" size="sm">
                      Finish this run
                    </Anchor>
                  </Link>{' '}
                  in the full editor.
                </>
              )}
            </Alert>
          )}
          {quickConversion.isSuccess && quickConversion.data && (
            <Alert color="green" icon={<IconCircleCheck size={16} />} title="Conversion posted">
              {quickConversion.data.post.summary} Cost: {formatMoneyCents(quickConversion.data.post.runCostCents)}.{' '}
              <Link to="/production-packconv/runs/$runId" params={{ runId: quickConversion.data.runId }}>
                <Anchor component="span" size="sm">
                  View run {quickConversion.data.runNumber}
                </Anchor>
              </Link>
            </Alert>
          )}

          <Group justify="flex-end">
            {quickConversion.isSuccess && (
              <Button variant="default" onClick={resetForm}>
                Record another conversion
              </Button>
            )}
            <Tooltip label="Requires Operations role" disabled={canOperate}>
              <span>
                <Button disabled={!canSubmit} loading={quickConversion.isPending} onClick={handleSubmit}>
                  Record conversion
                </Button>
              </span>
            </Tooltip>
          </Group>
        </Stack>
      </Paper>
    </Stack>
  );
}
